import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDuration } from "../../lib/format";
import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

export default function CalendarPage({ embed = false, tourId = null, tour = null, compact = false }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [instances, setInstances] = useState([]);
  const [tours, setTours] = useState({});
  const [selected, setSelected] = useState(null);
  const [filterTourId, setFilterTourId] = useState(tourId || "");

  useEffect(() => {
    const from = startOfMonth(cursor).toISOString();
    const to = new Date(endOfMonth(cursor).setHours(23,59,59)).toISOString();
    const tid = tourId || filterTourId;
    const url = `/instances?active_only=true&from_date=${from}&to_date=${to}${tid ? `&tour_id=${tid}` : ""}`;
    Promise.all([
      api.get(url),
      tourId ? Promise.resolve({ data: tour ? [tour] : [] }) : api.get("/tours?active_only=true"),
    ]).then(([i, t]) => {
      setInstances(i.data);
      setTours(Object.fromEntries(t.data.map(x => [x.id, x])));
    }).catch(() => {});
  }, [cursor, tourId, filterTourId, tour]);

  const grouped = useMemo(() => {
    const g = {};
    for (const ins of instances) {
      const d = new Date(ins.starts_at);
      const key = d.toISOString().slice(0,10);
      (g[key] ||= []).push(ins);
    }
    return g;
  }, [instances]);

  const days = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const blanks = first.getDay();
    const cells = [];
    for (let i = 0; i < blanks; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return cells;
  }, [cursor]);

  const selectedKey = selected?.toISOString().slice(0,10);
  const selectedItems = selectedKey ? (grouped[selectedKey] || []) : [];

  return (
    <div className={embed ? "embed-shell" : "public-shell"}>
      {!embed && !compact && <PublicNav />}
      <section className={`${compact ? "" : "max-w-6xl mx-auto"} px-6 ${compact ? "" : "md:px-12"} ${embed ? "pt-12" : (compact ? "pt-0" : "pt-32")} pb-16`}>
        {!embed && !compact && (
          <>
            <p className="label-eyebrow text-ms-brass">Reservations</p>
            <h1 className="serif text-ms-ink text-5xl md:text-6xl mt-4 leading-[0.95]">Choose your mystery.</h1>
            <p className="text-ms-stone mt-5 max-w-xl text-base leading-[1.7]">Pick a tour, then a date. Each tour keeps its own schedule — calm by design.</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="label-eyebrow text-ms-stone text-[10px]">Filter by tour:</span>
              <select value={filterTourId} onChange={e=>setFilterTourId(e.target.value)} data-testid="calendar-tour-filter"
                className="bg-ms-ivory border border-ms-edge rounded-md text-ms-ink px-4 py-2 text-sm focus:border-ms-brass outline-none">
                <option value="">All tours</option>
                {Object.values(tours).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
          </>
        )}

        <div className={`${compact ? "mt-2" : "mt-10"} grid grid-cols-1 ${compact ? "" : "lg:grid-cols-12"} gap-${compact ? "6" : "10"}`}>
          <div className={compact ? "" : "lg:col-span-7"}>
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()-1, 1))} className="label-eyebrow text-xs text-ms-stone hover:text-ms-brass" data-testid="cal-prev">← Prev</button>
              <h2 className="serif text-2xl md:text-3xl text-ms-ink" data-testid="cal-month">{monthNames[cursor.getMonth()]} {cursor.getFullYear()}</h2>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth()+1, 1))} className="label-eyebrow text-xs text-ms-stone hover:text-ms-brass" data-testid="cal-next">Next →</button>
            </div>
            <div className="grid grid-cols-7 gap-1.5 text-center label-eyebrow text-ms-stone text-[10px] mb-3">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {days.map((d, idx) => {
                if (!d) return <div key={idx} />;
                const k = d.toISOString().slice(0,10);
                const items = grouped[k] || [];
                const hasItems = items.length > 0;
                const allSold = hasItems && items.every(i => (i.capacity - (i.booked_count||0)) <= 0);
                const isPast = d < new Date(new Date().toDateString());
                const isSelected = selected && d.toDateString() === selected.toDateString();
                return (
                  <button
                    key={idx}
                    disabled={!hasItems || isPast}
                    onClick={() => setSelected(d)}
                    data-testid={`cal-day-${k}`}
                    className={`aspect-square rounded-sm text-sm flex flex-col items-center justify-center transition-all
                      ${isSelected ? "bg-ms-ink text-ms-cream" : "bg-ms-ivory border border-ms-edge text-ms-ink"}
                      ${hasItems && !isPast ? "hover:border-ms-brass cursor-pointer" : "opacity-40 cursor-not-allowed"}
                      ${allSold && !isSelected ? "opacity-50" : ""}
                    `}
                  >
                    <span>{d.getDate()}</span>
                    {hasItems && (
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full ${allSold ? "bg-ms-stone" : "bg-ms-brass"}`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={compact ? "" : "lg:col-span-5"}>
            <div className={compact ? "" : "bg-ms-ivory border border-ms-edge rounded-sm p-6 sticky top-28"}>
              <p className="label-eyebrow text-ms-brass">{selected ? selected.toLocaleDateString(undefined,{weekday:'long',month:'long',day:'numeric'}) : "Select a date"}</p>
              {selectedItems.length === 0 ? (
                <p className="mt-4 text-ms-stone text-sm">{selected ? "No tours that day." : "Click a date with a brass dot."}</p>
              ) : (
                <div className="mt-5 space-y-4">
                  {selectedItems.map(inst => {
                    const t = tours[inst.tour_id];
                    if (!t) return null;
                    const left = inst.capacity - (inst.booked_count || 0);
                    const time = new Date(inst.starts_at).toLocaleTimeString([], {hour:'numeric', minute:'2-digit'});
                    return (
                      <div key={inst.id} className="border-t border-ms-edge pt-4">
                        <p className="serif text-2xl text-ms-ink">{t.title}</p>
                        <p className="text-ms-stone text-sm mt-1">{time} · {formatDuration(t)}</p>
                        <p className="text-ms-ink text-sm mt-3">${inst.price_override ?? t.base_price} <span className="text-ms-stone">per guest</span></p>
                        {left <= 0 ? (
                          <p className="mt-3 label-eyebrow text-xs text-ms-stone">Sold out</p>
                        ) : (
                          <Link to={`/checkout/${inst.id}`} className="mt-3 inline-block px-5 py-2.5 bg-ms-ink text-ms-cream rounded-full hover:bg-ms-brass label-eyebrow text-xs transition-all" data-testid={`book-instance-${inst.id}`}>
                            Reserve · {left} left
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {!embed && !compact && <Footer />}
    </div>
  );
}
